import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Check, X, ExternalLink, Building2, Globe, DollarSign, Calendar, Tv, MapPin, Download, Upload } from 'lucide-react';
import { REJECTION_REASONS, REJECTION_REASONS_BY_CODE } from '../../components/self-service/rejectionReasons';
import type { SelfServiceCreativeRow, SelfServicePushJobFailure, SelfServiceSubmissionRow } from '../../types/selfService';
import { publisherStatusBadge } from '../../components/self-service/campaignSharedHelpers';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { apiClient } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import { formatMoneySmart, ensureAbsoluteUrl } from '../../components/self-service/shared/format';
import { formatDate } from '../../components/self-service/shared/dateFormat';
import { normalizePublicaFilename } from '../../components/self-service/shared/publicaFilename';

type SubmissionRow = SelfServiceSubmissionRow;

// CTV-457 Phase C — target of the reject modal. The same modal opens for
// whole-submission reject (single-creative path, existing behaviour) and
// per-creative reject (multi-creative path, new). The submit handler reads
// this to know which endpoint to call.
type RejectTarget =
  | { kind: 'submission' }
  | { kind: 'creative'; creativeId: string };

const PublisherSubmissionDetailPage: React.FC = () => {
  const { slug, config } = useSingleStationContext();
  const { submissionId } = useParams<{ submissionId: string }>();
  if (!submissionId) return <Navigate to={`/admin/${slug}/submissions`} replace />;
  return <Inner slug={slug} submissionId={submissionId} config={config} />;
};

const Inner: React.FC<{ slug: string; submissionId: string; config: import('../../types/selfService').PublisherConfig }> = ({ slug, submissionId, config }) => {
  const [row, setRow] = React.useState<SubmissionRow | null>(null);
  // CTV-457 Phase C: video URLs are now per-creative. Single-creative
  // submissions still have one entry in this map; multi-creative renders
  // one video element per entry.
  const [videoUrls, setVideoUrls] = React.useState<Record<string, string>>({});
  const [loading, setLoading] = React.useState(true);
  const [actionBusy, setActionBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = React.useState<RejectTarget | null>(null);
  const [rejectReason, setRejectReason] = React.useState('');
  const [rejectCodes, setRejectCodes] = React.useState<string[]>([]);

  // Publica upload gate — mirrors the flow in
  // frontend/src/components/rfp/UploadCreativesView.tsx so publishers see the
  // same steps they're used to. For Phase C, confirmation is per-creative so
  // the publisher walks through one download/upload per file.
  const [confirmedIds, setConfirmedIds] = React.useState<Set<string>>(new Set());
  const [publicaConfirmOpen, setPublicaConfirmOpen] = React.useState<
    | { kind: 'capture' }
    | { kind: 'approve-all' }
    | null
  >(null);
  // CTV-466: confirmation modal for "Send rejections to advertiser".
  const [sendRejectionsConfirmOpen, setSendRejectionsConfirmOpen] = React.useState(false);
  const [iframeLoading, setIframeLoading] = React.useState(true);
  const [iframeError, setIframeError] = React.useState(false);
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  // TODO(publica-id): no `publica_publisher_id` on selfservice_publishers yet —
  // fall back to the Publica root URL like UploadCreativesView does. Once we
  // add a per-publisher Publica ID, deep-link straight into their library.
  const publicaUrl = 'https://app.getpublica.com';

  // CTV-457 Phase C: the set of creatives the publisher reviews on this
  // page — active (already approved), pending_review (awaiting decision),
  // and UNRESOLVED voided (publisher rejected it and the buyer hasn't acted
  // yet; surfaced so they can see what they did). 'retired' lives in
  // history-only surfaces (advertiser Gantt); this page is a pending-action
  // queue, not a timeline.
  // CTV-611: once the buyer submits a replacement, resolved_at is stamped
  // and the rejected card drops out of this queue. On re-review the admin
  // only needs the newly submitted creatives, not a rejected one that will
  // never run.
  // API returns them already sorted active → pending_review → voided → retired.
  const reviewableCreatives: SelfServiceCreativeRow[] = React.useMemo(
    () =>
      (row?.creatives ?? []).filter(
        (c) =>
          c.status === 'active' ||
          c.status === 'pending_review' ||
          (c.status === 'voided' && !c.resolved_at),
      ),
    [row?.creatives],
  );
  const pendingCreatives = reviewableCreatives.filter((c) => c.status === 'pending_review');
  // CTV-487 follow-up: a running ('approved') campaign that has acquired
  // pending_review creatives via Edit Commercials is reviewed per-creative
  // (the campaign is already running — there's no submission to bulk-reject).
  // We route these to the multi-card surface so each pending creative gets
  // its own approve/reject controls.
  const isPostApprovalSwap = row?.status === 'approved' && pendingCreatives.length > 0;
  const isMulti = reviewableCreatives.length > 1 || isPostApprovalSwap;

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { rows } = await apiClient.post<{ rows: SubmissionRow[] }>(
        '/submissions/list-by-publisher',
        { slug },
      );
      const found = (rows ?? []).find((r) => r.id === submissionId) ?? null;
      setRow(found);

      // Per-creative view-url fetch. Single-creative submissions hit this
      // once; multi-creative hits it N times in parallel. view-url already
      // supports `creativeId` (creatives.service.ts) — passing it scopes the
      // signed URL to that exact row rather than the prefer-active default.
      // CTV-611: mirror the reviewableCreatives filter above so resolved
      // rejections don't get signed video URLs fetched either.
      const reviewable = (found?.creatives ?? []).filter(
        (c) =>
          c.status === 'active' ||
          c.status === 'pending_review' ||
          (c.status === 'voided' && !c.resolved_at),
      );
      const results = await Promise.allSettled(
        reviewable
          .filter((c) => !!c.storage_path)
          .map(async (c) => {
            const { url } = await apiClient.post<{ url: string }>('/creatives/view-url', {
              submissionId: found!.id,
              creativeId: c.id,
            });
            return [c.id, url] as const;
          }),
      );
      const urls: Record<string, string> = {};
      for (const r of results) {
        if (r.status === 'fulfilled') urls[r.value[0]] = r.value[1];
      }
      setVideoUrls(urls);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [slug, submissionId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const handleDownload = async (creative: SelfServiceCreativeRow) => {
    const url = videoUrls[creative.id];
    if (!url) return;
    setDownloadingId(creative.id);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = normalizePublicaFilename(creative.filename || 'creative.mp4');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const toggleConfirmed = (creativeId: string, value: boolean) => {
    setConfirmedIds((prev) => {
      const next = new Set(prev);
      if (value) next.add(creativeId);
      else next.delete(creativeId);
      return next;
    });
  };

  const captureSubmission = async () => {
    if (!row) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${row.id}/capture`, {});
      analytics.track(EVENTS.PAYMENT_CAPTURED, { station_slug: slug, submission_id: row.id });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally { setActionBusy(false); }
  };

  const approveAll = async () => {
    if (!row) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${row.id}/creatives/approve-all`, {});
      analytics.track(EVENTS.CREATIVE_APPROVED, { station_slug: slug, submission_id: row.id, scope: 'all' });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve-all failed');
    } finally { setActionBusy(false); }
  };

  const sendRejections = async () => {
    if (!row) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${row.id}/finalize-rejections`, {});
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send rejections failed');
    } finally { setActionBusy(false); }
  };

  const approveOne = async (creativeId: string) => {
    if (!row) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${row.id}/creatives/${creativeId}/approve`, {});
      analytics.track(EVENTS.CREATIVE_APPROVED, { station_slug: slug, submission_id: row.id, scope: 'single', creative_id: creativeId });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally { setActionBusy(false); }
  };

  const rejectHasOtherCode = rejectCodes.includes('other');
  const rejectCanSubmit =
    rejectCodes.length > 0 && (!rejectHasOtherCode || rejectReason.trim().length > 0);

  const submitReject = async () => {
    if (!row || !rejectTarget || !rejectCanSubmit) return;
    setActionBusy(true);
    setError(null);
    try {
      const body = {
        reasonCodes: rejectCodes,
        reason: rejectReason.trim() || undefined,
      };
      if (rejectTarget.kind === 'submission') {
        await apiClient.post(`/submissions/${row.id}/reject`, body);
        analytics.track(EVENTS.SUBMISSION_REJECTED, { station_slug: slug, submission_id: row.id, reason_codes: rejectCodes });
      } else {
        await apiClient.post(
          `/submissions/${row.id}/creatives/${rejectTarget.creativeId}/reject`,
          body,
        );
        analytics.track(EVENTS.CREATIVE_REJECTED, { station_slug: slug, submission_id: row.id, creative_id: rejectTarget.creativeId, reason_codes: rejectCodes });
      }
      setRejectTarget(null);
      setRejectReason('');
      setRejectCodes([]);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject failed');
    } finally { setActionBusy(false); }
  };

  const retryPush = async () => {
    if (!row) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${row.id}/retry-push`, {});
      analytics.track(EVENTS.PUSH_RETRIED, { station_slug: slug, submission_id: row.id });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Retry failed');
    } finally { setActionBusy(false); }
  };

  const productName = row && config
    ? (config.products.find((p) => p.id === row.campaign?.selectedProduct)?.name ?? row.campaign?.selectedProduct ?? '—')
    : '—';
  const marketNames = row && config
    ? (row.campaign?.targetMarkets || [])
        .map((code) => config.markets.find((m) => m.dmaCode === code)?.dmaName ?? code)
        .join(', ') || '—'
    : '—';

  // CTV-518 (merchant of record): the publisher's payout is campaign budget ×
  // (1 − rev share), released 60–90 days after the charge — NOT total minus
  // service fee (that was the Express destination-charge model). Campaign
  // budget prefers budget_cents and falls back to total − fee − tax for older
  // rows. CTV-577: the customer-total ("held on card") is service-fee inclusive
  // and irrelevant to the publisher, so it's no longer surfaced here.
  const mediaBudgetCents = row
    ? row.budget_cents ?? row.amount_cents - row.service_fee_cents - (row.tax_cents ?? 0)
    : 0;
  const mediaBudget = (mediaBudgetCents / 100).toFixed(2);
  const revShare = row?.rev_share_percent ?? null;
  const yourShare =
    revShare != null ? (Math.round(mediaBudgetCents * (1 - revShare)) / 100).toFixed(2) : null;
  // CTV-577: monthly campaigns pay the share out across installments, so the
  // figure is the full-campaign share — annotate the span so it doesn't read
  // as a single charge. Upfront (≤1 installment) gets no suffix.
  const installmentMonths = row?.installments?.length ?? 0;
  const yourShareSpan = installmentMonths > 1 ? ` over ${installmentMonths} months` : '';
  const canAct = row?.status === 'payment_authorized' || isPostApprovalSwap;
  const hasVoidedCreative = reviewableCreatives.some((c) => c.status === 'voided');
  // CTV-466: once the publisher has decided on every creative — none left
  // pending, at least one voided — the sticky action flips from "Approve all"
  // to "Send rejections to advertiser".
  const isFinalizeReady = hasVoidedCreative && pendingCreatives.length === 0;
  const allPendingConfirmed =
    pendingCreatives.length > 0 && pendingCreatives.every((c) => confirmedIds.has(c.id));
  // Single-creative path uses creatives[0] (active or pending). The Approve
  // button gates on its Publica confirmation. We use the same id-keyed Set
  // so single + multi share state.
  const singleCreative = !isMulti ? reviewableCreatives[0] ?? null : null;
  const singleConfirmed = !!singleCreative && confirmedIds.has(singleCreative.id);
  const singlePublicaFilename = singleCreative
    ? normalizePublicaFilename(singleCreative.filename || 'creative.mp4')
    : 'creative.mp4';

  return (
    <div className="max-w-5xl">
      <Link to={`/admin/${slug}/submissions`} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to submissions
      </Link>

        {loading ? (
          <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : !row ? (
          <p className="text-sm text-slate-700">Submission not found.</p>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  {row.business_info?.businessName || 'Advertiser'}
                </div>
                <h1 className="text-2xl font-semibold text-slate-900">
                  {row.campaign?.campaignName || row.business_info?.businessName || '—'}
                </h1>
                <p className="text-sm text-slate-600 mt-1">Submitted {new Date(row.created_at).toLocaleString()}</p>
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded ${publisherStatusBadge(row.status).className}`}>{publisherStatusBadge(row.status).label}</span>
            </div>

            {error && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</p>}
            {row.pushJobFailure?.jobKind === 'create' && row.pushJobFailure.reason === 'creative_not_in_library' && (() => {
              // Match the failed file against this submission's creatives so
              // the publisher can download the exact MP4 they need to upload.
              // Fallback: if filename doesn't match (older job or rename),
              // surface download for every reviewable creative.
              const failedFilename = row.pushJobFailure.filename ?? null;
              const failedCreative = failedFilename
                ? reviewableCreatives.find(
                    (c) => normalizePublicaFilename(c.filename) === normalizePublicaFilename(failedFilename),
                  ) ?? null
                : null;
              const recoveryCreatives = failedCreative ? [failedCreative] : reviewableCreatives;
              return (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg overflow-hidden text-sm">
                  <div className="p-4 border-b border-red-200">
                    <div className="font-semibold text-red-900 mb-1">Push to Publica failed</div>
                    <p className="text-red-800">
                      The creative{' '}
                      <span className="font-mono">{failedFilename ?? 'file'}</span>{' '}
                      wasn&apos;t found in Publica&apos;s creative library. Download it below, upload it to
                      Publica, then click <strong>Retry push</strong>.
                    </p>
                  </div>

                  <div className="p-4 bg-white space-y-3 border-b border-red-200">
                    {recoveryCreatives.map((c) => {
                      const publicaName = normalizePublicaFilename(c.filename || 'creative.mp4');
                      return (
                        <div key={c.id} className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-mono text-xs text-slate-700 truncate">{c.filename}</div>
                            <div className="text-[11px] text-slate-500">
                              Saves as <span className="font-mono">{publicaName}</span>
                              {c.size_bytes ? <> · {(c.size_bytes / (1024 * 1024)).toFixed(1)} MB</> : null}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDownload(c)}
                            disabled={!videoUrls[c.id] || downloadingId === c.id}
                            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md disabled:opacity-60"
                          >
                            {downloadingId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            Download
                          </button>
                        </div>
                      );
                    })}

                    <ol className="list-decimal list-inside text-[13px] text-slate-700 space-y-1 pt-2 border-t border-slate-200">
                      <li>Log in to Publica below (or open in a new tab if the embed doesn&apos;t load).</li>
                      <li>Go to <strong>Campaigns &gt; Creative Library</strong> and click <strong>Add Asset</strong>.</li>
                      <li>Upload the MP4{recoveryCreatives.length > 1 ? 's' : ''} you just downloaded.</li>
                      <li>Click <strong>Retry push</strong> below.</li>
                    </ol>
                  </div>

                  <div className="border-b border-red-200">
                    <div className="flex items-center justify-between px-4 py-2 bg-slate-50">
                      <h3 className="text-sm font-semibold text-slate-800">Publica</h3>
                      <a
                        href={publicaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                      >
                        Open in new tab <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="relative bg-white" style={{ height: '600px' }}>
                      {iframeLoading && !iframeError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-10">
                          <div className="text-center">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">Loading Publica…</p>
                          </div>
                        </div>
                      )}
                      {iframeError ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-amber-50 p-6">
                          <div className="text-center max-w-md">
                            <p className="text-sm text-amber-800 mb-3">
                              Publica could not be loaded in the embedded view. This can happen due to browser security settings.
                            </p>
                            <a
                              href={publicaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                            >
                              Open Publica in new tab
                            </a>
                          </div>
                        </div>
                      ) : (
                        <iframe
                          src={publicaUrl}
                          title="Publica"
                          className="w-full h-full border-0"
                          onLoad={() => setIframeLoading(false)}
                          onError={() => { setIframeLoading(false); setIframeError(true); }}
                          allow="clipboard-write"
                        />
                      )}
                    </div>
                  </div>

                  <div className="p-4 bg-white flex items-center justify-end">
                    <button
                      type="button"
                      onClick={retryPush}
                      disabled={actionBusy}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Retry push
                    </button>
                  </div>
                </div>
              );
            })()}
            {row.status === 'creative_rejected' && row.rejection_reason && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <div className="font-semibold text-red-900">Rejection reason</div>
                  {row.reviewed_at && (
                    <div className="text-xs text-red-700">
                      Rejected {new Date(row.reviewed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </div>
                  )}
                </div>
                <p className="text-red-800">{row.rejection_reason}</p>
              </div>
            )}

            {/* Details strip — moved up top so the page flows
                video → Publica step without an interrupting sidebar. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Section title="Advertiser">
                <Field icon={Building2} label="Business" value={row.business_info?.businessName || '—'} />
                {row.business_info?.businessWebsite && <Field icon={Globe} label="Website" value={row.business_info.businessWebsite} />}
              </Section>

              <Section title="Campaign">
                <Field icon={DollarSign} label="Budget" value={row.campaign?.budget ? `$${formatMoneySmart(row.campaign.budget)}` : '—'} />
                <Field icon={Tv} label="Product" value={productName} />
                <Field icon={Calendar} label="Start" value={formatDate(row.campaign?.startDate)} />
                <Field icon={Calendar} label="End" value={formatDate(row.campaign?.endDate)} />
                <Field icon={MapPin} label="Markets" value={marketNames} scrollable />
              </Section>

              <Section title="Payment">
                <Field label="Campaign budget" value={`$${mediaBudget}`} />
                {yourShare != null && <Field label="Your share" value={`$${yourShare}${yourShareSpan}`} />}
                {/* TODO(CTV-577): payout timing copy is hidden until we
                    implement payouts based on delivered impressions — revisit
                    and reword ("Released 60–90 days after payment." no longer
                    reflects the model) once that's built. */}
                {/* <p className="text-xs text-slate-500 pt-1">
                  Released 60–90 days after payment.
                </p> */}
                {row.stripe_payment_intent_id && (
                  <div className="pt-1">
                    <a href={`https://dashboard.stripe.com/test/payments/${row.stripe_payment_intent_id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 font-mono">
                      View on Stripe <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </Section>
            </div>

            {/* Creative — full width below the details strip so it leads
                straight into the Publica upload step. Single-creative path
                renders today's UI verbatim; multi-creative renders one card
                per creative with per-card Approve / Reject controls. */}
            {isMulti ? (
              <MultiCreativeView
                creatives={reviewableCreatives}
                videoUrls={videoUrls}
                confirmedIds={confirmedIds}
                downloadingId={downloadingId}
                actionBusy={actionBusy}
                canAct={canAct}
                pushJobFailure={row.pushJobFailure ?? null}
                onDownload={handleDownload}
                onToggleConfirmed={toggleConfirmed}
                onApprove={(creativeId) => approveOne(creativeId)}
                onReject={(creativeId) => {
                  setRejectTarget({ kind: 'creative', creativeId });
                  setRejectCodes([]);
                  setRejectReason('');
                }}
              />
            ) : (
              <div className="mb-6">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Creative</h2>
                {singleCreative && videoUrls[singleCreative.id] ? (
                  <video src={videoUrls[singleCreative.id]} controls className="w-full rounded-lg bg-black aspect-video max-w-3xl" />
                ) : singleCreative?.storage_path ? (
                  <div className="bg-slate-100 rounded-lg p-10 text-center text-sm text-slate-500 max-w-3xl"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading video…</div>
                ) : (
                  <div className="bg-slate-100 rounded-lg p-10 text-center text-sm text-slate-500 max-w-3xl">No video uploaded</div>
                )}
                <div className="mt-2 text-xs text-slate-500">
                  {singleCreative?.filename}
                  {singleCreative?.size_bytes && <> · {(singleCreative.size_bytes / (1024 * 1024)).toFixed(1)} MB</>}
                </div>
                {row.campaign?.clickThroughUrl && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Click-through URL</div>
                    <a href={ensureAbsoluteUrl(row.campaign.clickThroughUrl)} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                      {row.campaign.clickThroughUrl} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                )}
              </div>
            )}

            {canAct && (
              <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-2 mb-1">
                  <Upload className="w-4 h-4 text-blue-600" />
                  <h2 className="text-base font-semibold text-slate-900">
                    {isMulti
                      ? `Step 1: Upload ${pendingCreatives.length} creative${pendingCreatives.length === 1 ? '' : 's'} to Publica`
                      : 'Step 1: Upload creative to Publica'}
                  </h2>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  {isMulti
                    ? 'Before approving, copy each pending creative into your Publica creative library. Download and confirm each one below.'
                    : "Before approving, copy this advertiser's video into your Publica creative library so it can be trafficked."}
                </p>

                {!isMulti && singleCreative && (
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={() => handleDownload(singleCreative)}
                      disabled={!videoUrls[singleCreative.id] || downloadingId === singleCreative.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md disabled:opacity-60"
                    >
                      {downloadingId === singleCreative.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      Download video
                    </button>
                    {singleCreative?.filename && (
                      <span className="ml-2 text-xs text-slate-500">
                        Saves as <span className="font-mono">{singlePublicaFilename}</span>
                      </span>
                    )}
                  </div>
                )}

                <ol className="list-decimal list-inside text-sm text-slate-700 space-y-2 mb-5">
                  <li>
                    {isMulti
                      ? "Download each pending creative using the buttons on its card above."
                      : "Download the advertiser's video using the button above."}
                  </li>
                  <li>Log in to Publica below (or use the &quot;Open in new tab&quot; link if the login doesn&apos;t load).</li>
                  <li>Navigate to <strong>Campaigns &gt; Creative Library</strong>.</li>
                  <li>Click <strong>Add Asset</strong> in the top right corner.</li>
                  <li>
                    {isMulti
                      ? "Select Upload Media File(s) and upload each MP4 you downloaded."
                      : "Select Upload Media File(s) and upload the MP4 you just downloaded."}
                  </li>
                  {isMulti && (
                    <li>Tick the &quot;Uploaded to Publica&quot; checkbox on each card above once it&apos;s in the library.</li>
                  )}
                </ol>

                <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
                    <h3 className="text-sm font-semibold text-slate-800">Publica</h3>
                    <a
                      href={publicaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                    >
                      Open in new tab <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="relative" style={{ height: '700px' }}>
                    {iframeLoading && !iframeError && (
                      <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-10">
                        <div className="text-center">
                          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                          <p className="text-sm text-slate-500">Loading Publica…</p>
                        </div>
                      </div>
                    )}
                    {iframeError ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-amber-50 p-6">
                        <div className="text-center max-w-md">
                          <p className="text-sm text-amber-800 mb-3">
                            Publica could not be loaded in the embedded view. This can happen due to browser security settings.
                          </p>
                          <a
                            href={publicaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                          >
                            Open Publica in new tab
                          </a>
                        </div>
                      </div>
                    ) : (
                      <iframe
                        src={publicaUrl}
                        title="Publica"
                        className="w-full h-full border-0"
                        onLoad={() => setIframeLoading(false)}
                        onError={() => { setIframeLoading(false); setIframeError(true); }}
                        allow="clipboard-write"
                      />
                    )}
                  </div>
                </div>

                {!isMulti && singleCreative && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={singleConfirmed}
                      onChange={(e) => toggleConfirmed(singleCreative.id, e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700">
                      I have uploaded this creative to Publica.
                    </span>
                  </label>
                )}
              </div>
            )}

            {canAct && !isPostApprovalSwap && (
              <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-10 px-10 md:pr-52 py-4 flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  {isMulti
                    ? (isFinalizeReady
                        ? `Ready — send rejections to the advertiser to release the authorization hold.`
                        : (hasVoidedCreative
                            ? `Mixed — review the remaining ${pendingCreatives.length} pending creative(s).`
                            : (allPendingConfirmed
                                ? `Ready — approve all ${pendingCreatives.length} creatives to capture payment.`
                                : 'Confirm the Publica upload on each card above to enable Approve all.')))
                    : (singleConfirmed
                        ? 'Ready — approve to capture payment, or reject if the creative isn\'t suitable.'
                        : 'Confirm the Publica upload above to enable Approve.')}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setRejectTarget({ kind: 'submission' });
                      setRejectCodes([]);
                      setRejectReason('');
                    }}
                    disabled={actionBusy}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 hover:bg-red-50 rounded-md disabled:opacity-60"
                  >
                    <X className="w-4 h-4" />
                    {isMulti ? 'Reject submission' : 'Reject'}
                  </button>
                  {isMulti ? (
                    isFinalizeReady ? (
                      <button
                        onClick={() => setSendRejectionsConfirmOpen(true)}
                        disabled={actionBusy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Send rejections to advertiser
                      </button>
                    ) : (
                      <button
                        onClick={() => setPublicaConfirmOpen({ kind: 'approve-all' })}
                        disabled={
                          actionBusy ||
                          hasVoidedCreative ||
                          pendingCreatives.length === 0 ||
                          !allPendingConfirmed
                        }
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Approve all &amp; charge
                      </button>
                    )
                  ) : (
                    <button
                      onClick={() => setPublicaConfirmOpen({ kind: 'capture' })}
                      disabled={actionBusy || !singleConfirmed}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Approve &amp; charge
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* "Did all creatives upload successfully?" — same wording as the
                dashboard's UploadCreativesView so the publisher gets the
                familiar safety check before we capture the charge. */}
            {publicaConfirmOpen && (
              <>
                <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionBusy && setPublicaConfirmOpen(null)} />
                <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 p-5">
                  <h3 className="text-base font-semibold text-slate-900 mb-1">
                    {publicaConfirmOpen.kind === 'approve-all'
                      ? 'Did all creatives upload successfully?'
                      : 'Did the creative upload successfully?'}
                  </h3>
                  <p className="text-sm text-slate-600 mb-5">
                    Please confirm {publicaConfirmOpen.kind === 'approve-all' ? 'every creative shows' : "the creative shows"} as successfully uploaded in Publica before continuing. If anything&apos;s still processing, wait until it&apos;s complete, then proceed.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setPublicaConfirmOpen(null)}
                      disabled={actionBusy}
                      className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      No, wait for upload
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const target = publicaConfirmOpen;
                        setPublicaConfirmOpen(null);
                        if (target?.kind === 'approve-all') await approveAll();
                        else await captureSubmission();
                      }}
                      disabled={actionBusy}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {actionBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                      Yes, approve &amp; charge
                    </button>
                  </div>
                </div>
              </>
            )}

            {sendRejectionsConfirmOpen && (
              <>
                <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionBusy && setSendRejectionsConfirmOpen(false)} />
                <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 p-5">
                  <h3 className="text-base font-semibold text-slate-900 mb-1">
                    Send rejections to advertiser?
                  </h3>
                  <p className="text-sm text-slate-600 mb-5">
                    We&apos;ll email the advertiser with reviewer notes for {reviewableCreatives.filter((c) => c.status === 'voided').length} rejected creative{reviewableCreatives.filter((c) => c.status === 'voided').length === 1 ? '' : 's'}, release the authorization hold, and ask the advertiser to fix the affected commercials. The submission will move to <span className="font-medium text-slate-900">creative rejected</span>.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setSendRejectionsConfirmOpen(false)}
                      disabled={actionBusy}
                      className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        setSendRejectionsConfirmOpen(false);
                        await sendRejections();
                      }}
                      disabled={actionBusy}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {actionBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                      Yes, send rejections
                    </button>
                  </div>
                </div>
              </>
            )}

            {rejectTarget && (
              <>
                <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionBusy && setRejectTarget(null)} />
                <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-lg bg-white rounded-xl shadow-2xl border border-slate-200 p-5 max-h-[85vh] overflow-y-auto">
                  <h3 className="text-base font-semibold text-slate-900 mb-1">
                    {rejectTarget.kind === 'submission' ? 'Reject submission' : 'Reject this creative'}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Pick every reason that applies. The advertiser will see these on their campaigns page.
                  </p>

                  <div className="space-y-2 mb-4">
                    {REJECTION_REASONS.map((r) => {
                      const checked = rejectCodes.includes(r.code);
                      return (
                        <label
                          key={r.code}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            checked ? 'border-red-300 bg-red-50/60' : 'border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setRejectCodes((prev) =>
                                e.target.checked ? [...prev, r.code] : prev.filter((c) => c !== r.code),
                              );
                            }}
                            className="mt-0.5 w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                          />
                          <span className="text-sm text-slate-900 font-medium">{r.label}</span>
                        </label>
                      );
                    })}
                  </div>

                  <label className="block">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {rejectHasOtherCode ? 'Note (required for Other)' : 'Optional note to advertiser'}
                    </span>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder={rejectHasOtherCode
                        ? 'Describe the issue so the advertiser can fix and resubmit.'
                        : 'Anything extra you want the advertiser to know.'}
                      rows={3}
                      className="mt-1 w-full p-3 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    />
                  </label>

                  <div className="flex justify-end gap-2 mt-4">
                    <button onClick={() => setRejectTarget(null)} disabled={actionBusy} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
                    <button onClick={submitReject} disabled={actionBusy || !rejectCanSubmit} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-md inline-flex items-center gap-1.5">
                      {actionBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                      {rejectTarget.kind === 'submission' ? 'Reject submission' : 'Reject creative'}
                    </button>
                  </div>
                </div>
              </>
            )}
        </>
      )}
    </div>
  );
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{title}</h3>
      <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-lg p-3">{children}</div>
    </div>
  );
}

function Field({
  icon: Icon,
  label,
  value,
  scrollable = false,
}: {
  icon?: React.FC<{ className?: string }>;
  label: string;
  value: string;
  // CTV-466 follow-up: when the value is a very long comma-joined list
  // (e.g. 200+ market ZIPs on a network campaign), clip to a max height and
  // scroll within the field so the surrounding page doesn't get pushed
  // off-screen.
  scrollable?: boolean;
}) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="text-xs text-slate-500">{label}</div>
        {value && (
          <div
            className={`text-sm text-slate-900 break-words ${
              scrollable ? 'max-h-48 overflow-y-auto pr-1' : ''
            }`}
          >
            {value}
          </div>
        )}
      </div>
    </div>
  );
}

// CTV-457 Phase C: multi-creative review view. Renders one card per creative
// with per-card video, metadata, download + Publica confirmation, and
// Approve / Reject controls. Voided creatives render in a "rejected" state
// so the publisher can see what they already did.
function MultiCreativeView({
  creatives,
  videoUrls,
  confirmedIds,
  downloadingId,
  actionBusy,
  canAct,
  pushJobFailure,
  onDownload,
  onToggleConfirmed,
  onApprove,
  onReject,
}: {
  creatives: SelfServiceCreativeRow[];
  videoUrls: Record<string, string>;
  confirmedIds: Set<string>;
  downloadingId: string | null;
  actionBusy: boolean;
  canAct: boolean;
  pushJobFailure: SelfServicePushJobFailure | null;
  onDownload: (c: SelfServiceCreativeRow) => void;
  onToggleConfirmed: (id: string, value: boolean) => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}) {
  return (
    <div className="mb-6">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Creatives ({creatives.length})
        </h2>
        <p className="text-xs text-slate-500">
          Approve or reject each creative below, or use Approve all at the bottom.
        </p>
      </div>
      <div className="space-y-4">
        {creatives.map((c) => (
          <CreativeReviewCard
            key={c.id}
            creative={c}
            videoUrl={videoUrls[c.id] ?? null}
            confirmed={confirmedIds.has(c.id)}
            downloading={downloadingId === c.id}
            actionBusy={actionBusy}
            canAct={canAct}
            pushJobFailure={pushJobFailure}
            onDownload={() => onDownload(c)}
            onToggleConfirmed={(v) => onToggleConfirmed(c.id, v)}
            onApprove={() => onApprove(c.id)}
            onReject={() => onReject(c.id)}
          />
        ))}
      </div>
    </div>
  );
}

function CreativeReviewCard({
  creative,
  videoUrl,
  confirmed,
  downloading,
  actionBusy,
  canAct,
  pushJobFailure,
  onDownload,
  onToggleConfirmed,
  onApprove,
  onReject,
}: {
  creative: SelfServiceCreativeRow;
  videoUrl: string | null;
  confirmed: boolean;
  downloading: boolean;
  actionBusy: boolean;
  canAct: boolean;
  pushJobFailure: SelfServicePushJobFailure | null;
  onDownload: () => void;
  onToggleConfirmed: (value: boolean) => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  const publicaFilename = normalizePublicaFilename(creative.filename || 'creative.mp4');
  // CTV-611: the edit-level swap banner is gone; a creative_swap push that
  // failed because the file wasn't in Publica's library reverts the creative
  // to pending_review, so it reappears here. Surface the failure tag on the
  // matching card so the publisher knows to upload the file before
  // re-approving. The failure's filename is the creative row's raw filename.
  const hasFailedPush =
    creative.status === 'pending_review' &&
    pushJobFailure?.jobKind === 'creative_swap' &&
    pushJobFailure.reason === 'creative_not_in_library' &&
    pushJobFailure.filename === creative.filename;
  const statusChip = (() => {
    if (creative.status === 'active') {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Approved</span>;
    }
    if (creative.status === 'voided') {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-red-100 text-red-800">Rejected</span>;
    }
    if (creative.status === 'retired') {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">Retired</span>;
    }
    return <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">Pending review</span>;
  })();

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-slate-900 truncate">{creative.filename}</span>
          {statusChip}
        </div>
        <div className="text-xs text-slate-500 whitespace-nowrap">
          Weight {(creative.segment_shares ?? []).map((s) => `${s.share}%`).join(' · ') || '—'} · {formatDate(creative.creative_start_date)} – {formatDate(creative.creative_end_date)}
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          {videoUrl ? (
            <video src={videoUrl} controls className="w-full rounded-lg bg-black aspect-video" />
          ) : creative.storage_path ? (
            <div className="bg-slate-100 rounded-lg p-6 text-center text-sm text-slate-500 aspect-video flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading video…
            </div>
          ) : (
            <div className="bg-slate-100 rounded-lg p-6 text-center text-sm text-slate-500 aspect-video flex items-center justify-center">No video uploaded</div>
          )}
          {creative.size_bytes && (
            <div className="mt-1 text-xs text-slate-500">{(creative.size_bytes / (1024 * 1024)).toFixed(1)} MB</div>
          )}
          {creative.click_through_url && (
            <div className="mt-2">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Click-through</div>
              <a href={ensureAbsoluteUrl(creative.click_through_url)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 break-all">
                {creative.click_through_url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            </div>
          )}
        </div>
        <div>
          {creative.status === 'voided' && (
            <div className="mb-3 text-sm bg-red-50 border border-red-200 rounded p-3">
              <div className="font-semibold text-red-900 mb-1">You rejected this creative</div>
              {creative.rejection_reason_codes && creative.rejection_reason_codes.length > 0 && (
                <ul className="list-disc list-inside text-red-800 mb-1">
                  {creative.rejection_reason_codes.map((code) => (
                    <li key={code}>{REJECTION_REASONS_BY_CODE[code]?.label ?? code}</li>
                  ))}
                </ul>
              )}
              {creative.rejection_reason && (
                <p className="text-red-800 whitespace-pre-wrap">{creative.rejection_reason}</p>
              )}
            </div>
          )}
          {hasFailedPush && (
            <div className="mb-3 border border-red-300 bg-red-50 rounded-md p-3">
              <p className="text-xs font-semibold text-red-900 mb-1">Previous push attempt failed</p>
              <p className="text-xs text-red-800">
                The last approval was rolled back because{' '}
                <span className="font-mono">{creative.filename}</span>{' '}
                wasn&apos;t found in Publica&apos;s creative library. Upload it into the Publica
                library, then approve again to retry.
              </p>
            </div>
          )}
          {creative.status === 'pending_review' && canAct && (
            <>
              <button
                type="button"
                onClick={onDownload}
                disabled={!videoUrl || downloading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md disabled:opacity-60"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download
              </button>
              <div className="mt-1 text-xs text-slate-500">
                Saves as <span className="font-mono">{publicaFilename}</span>
              </div>
              <label className="flex items-start gap-2 cursor-pointer mt-3">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => onToggleConfirmed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">
                  I uploaded this creative to Publica.
                </span>
              </label>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={onReject}
                  disabled={actionBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 hover:bg-red-50 rounded-md disabled:opacity-60"
                >
                  <X className="w-4 h-4" />
                  Reject
                </button>
                <button
                  type="button"
                  onClick={onApprove}
                  disabled={actionBusy || !confirmed}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Check className="w-4 h-4" />
                  Approve
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PublisherSubmissionDetailPage;
